import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Flame, ArrowRight, Zap, TrendingUp, TrendingDown, Trophy, MessageCircle, Sparkles, Clock, Target, Briefcase, Compass } from "lucide-react";
import { api, track } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { firstName } from "@/lib/name";
import { showWinCard } from "@/components/WinCard";
import { MissionMomentum, QuestCheckpoint } from "@/components/Retention";
import { AnimatedNumber } from "@/components/AnimatedNumber";

const fade = (d = 0) => ({ initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { delay: d, duration: 0.45, ease: [0.16, 1, 0.3, 1] } });

function Card({ children, className = "", testId, delay = 0, onClick }) {
  return (
    <motion.div {...fade(delay)} data-testid={testId} onClick={onClick}
      className={`rounded-3xl border border-border bg-card p-5 ${onClick ? "cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md" : ""} ${className}`}>
      {children}
    </motion.div>
  );
}

function Label({ children, className = "" }) {
  return <div className={`text-[11px] font-bold uppercase tracking-[0.2em] ${className}`}>{children}</div>;
}

export default function Home() {
  const [os, setOs] = useState(null);
  const navigate = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/os").then(({ data }) => {
      setOs(data);
      const k = `nxt_level_${user?.id || "me"}`;
      const last = Number(localStorage.getItem(k) || 0);
      if (last && data.level > last) setTimeout(() => showWinCard({ kind: "level_up", level: data.level, xp: data.xp }), 600);
      localStorage.setItem(k, String(data.level));
    }).catch(() => {});
  }, [user?.id]);
  if (!os) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? t("gm") : hour < 18 ? t("ga") : t("ge");
  const m = os.todays_mission;
  const q = os.current_quest;
  const gap = os.skill_gap;
  const delta = os.readiness_delta || 0;
  const startMission = () => { track("mission_started", { mission: m.key }); navigate("/app/missions"); };

  return (
    <div className="space-y-4">
      <motion.div {...fade(0)}>
        <h1 className="font-display text-3xl font-extrabold tracking-tight [overflow-wrap:anywhere]" data-testid="home-personal-greeting">{greeting}, {os.name} 👋</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("osTagline")}</p>
      </motion.div>

      {/* WHERE AM I → level + streak */}
      <div className="grid grid-cols-3 gap-3">
        <Card delay={0.05} testId="level-card" className="col-span-2 !border-0 bg-gradient-to-br from-indigo-600 to-violet-600 text-white">
          <Label className="text-white/60">{t("careerLevel")}</Label>
          <div className="mt-1 flex items-end justify-between">
            <div className="font-display text-5xl font-extrabold leading-none"><AnimatedNumber value={os.level} /></div>
            <div className="text-right font-mono text-xs text-white/70">{os.xp_in_level} / {os.xp_need} XP</div>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/20">
            <motion.div className="h-full rounded-full bg-white" initial={{ width: 0 }} animate={{ width: `${(os.xp_in_level / os.xp_need) * 100}%` }} transition={{ duration: 1 }} />
          </div>
        </Card>
        <Card delay={0.08} testId="streak-card" className="flex flex-col justify-between">
          <div className="flex items-center gap-1 text-amber-500"><Flame className="h-4 w-4" /><Label>{t("streak")}</Label></div>
          <div className="font-display text-4xl font-extrabold leading-none"><AnimatedNumber value={os.streak} /></div>
          <div className="text-xs text-muted-foreground">{t("days")}</div>
        </Card>
      </div>

      {/* WHERE AM I GOING → readiness toward target */}
      <Card delay={0.12} testId="readiness-card" onClick={() => navigate("/app/career")}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <Label className="text-emerald-600">{t("careerReadiness")}</Label>
            <div className="mt-1 font-display text-5xl font-extrabold leading-none"><AnimatedNumber value={os.readiness} suffix="%" /></div>
            {os.target_career && <div className="mt-2 text-sm font-bold uppercase tracking-wide text-indigo-600" data-testid="target-career">{os.target_career}</div>}
          </div>
          <div className="text-right">
            {delta !== 0 && (
              <div data-testid="readiness-delta" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${delta > 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                {delta > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />} {delta > 0 ? "+" : ""}{delta}% {t("thisMonth")}
              </div>
            )}
            {delta === 0 && <div className="text-xs text-muted-foreground">{t("readinessHint")}</div>}
          </div>
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-secondary">
          <motion.div className="h-full rounded-full bg-emerald-500" initial={{ width: 0 }} animate={{ width: `${os.readiness}%` }} transition={{ duration: 1 }} />
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">{t("readinessNote")}</p>
      </Card>

      <MissionMomentum status={os.streak_status} readiness={os.readiness} missionTitle={m.title} />
      {/* WHAT SHOULD I DO TODAY → mission */}
      <Card delay={0.16} testId="todays-mission-card" className="!border-indigo-200 bg-indigo-50/40">
        <div className="flex items-center justify-between">
          <Label className="text-indigo-600">{firstName(user) ? `${firstName(user)}'s Daily Mission` : t("todaysMission")}</Label>
          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">+{m.xp} XP</span>
        </div>
        <h2 className="mt-2 font-display text-xl font-bold leading-snug">{m.title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{m.description}</p>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {m.time_min} min</span>
          {m.reason && <span className="flex items-center gap-1 font-semibold text-indigo-600" data-testid="mission-reason"><Target className="h-3.5 w-3.5" /> {m.reason}</span>}
        </div>
        {m.completed_today ? (
          <div className="mt-4 flex items-center justify-center gap-2 rounded-full bg-emerald-50 py-3 font-bold text-emerald-700"><Zap className="h-5 w-5" /> {t("missionDone")}</div>
        ) : (
          <button onClick={startMission} data-testid="start-mission-btn"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 py-3.5 font-bold text-white transition-colors hover:bg-indigo-600">
            {t("startMission")} <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </Card>

      {/* WHAT AM I MISSING + CURRENT QUEST */}
      <div className="grid gap-3 sm:grid-cols-2">
        {gap && (
          <Card delay={0.2} testId="skill-gap-card" onClick={() => navigate("/app/missions")}>
            <Label className="text-rose-500">{t("biggestGap")}</Label>
            <div className="mt-1 flex items-end justify-between">
              <div className="font-display text-xl font-bold">{gap.label}</div>
              <div className="font-mono text-2xl font-extrabold text-rose-500">{gap.score}%</div>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-rose-400" style={{ width: `${gap.score}%` }} /></div>
            <div className="mt-3 flex items-center gap-1 text-sm font-bold text-indigo-600">{t("workOnIt")} <ArrowRight className="h-4 w-4" /></div>
          </Card>
        )}
        <Card delay={0.24} testId="current-quest-card" onClick={() => navigate("/app/missions?tab=quests")}>
          <Label className="text-amber-600">{t("currentQuest")}</Label>
          {q ? (
            <>
              <div className="mt-1 font-display text-lg font-bold leading-snug">{q.title}</div>
              <div className="mt-1 text-xs text-muted-foreground">{t("day")} {q.day} / {q.total_days} · {t("next")}: {q.next_level}</div>
              <QuestCheckpoint questKey={q.key} checkpoint={q.checkpoint} done={q.completed_levels} total={q.levels} compact />
              <div className="mt-3 flex items-center gap-1 text-sm font-bold text-indigo-600">{t("continueQuest")} <ArrowRight className="h-4 w-4" /></div>
            </>
          ) : (
            <>
              <div className="mt-1 font-display text-lg font-bold">{t("noQuest")}</div>
              <div className="mt-1 text-xs text-muted-foreground">{t("noQuestSub")}</div>
              <div className="mt-3 flex items-center gap-1 text-sm font-bold text-indigo-600">{t("browseQuests")} <ArrowRight className="h-4 w-4" /></div>
            </>
          )}
        </Card>
      </div>

      {/* WHAT HAVE I ACHIEVED → proof ; WHAT NEXT → coach */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Card delay={0.28} testId="proof-card" onClick={() => navigate("/app/proof")}>
          <div className="flex items-center gap-2 text-indigo-600"><Briefcase className="h-4 w-4" /><Label>{t("careerProof")}</Label></div>
          <div className="mt-1 font-display text-2xl font-extrabold">{os.proof.completed} <span className="text-base font-bold text-muted-foreground">/ {os.proof.total}</span></div>
          <div className="text-xs text-muted-foreground">{os.proof.completed === 0 ? t("proofEmpty") : t("proofBuilt")}</div>
          <div className="mt-2 flex gap-1">{Array.from({ length: os.proof.total }).map((_, i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i < os.proof.completed ? "bg-indigo-600" : "bg-secondary"}`} />)}</div>
        </Card>
        <Card delay={0.32} testId="home-coach-btn" onClick={() => navigate("/app/coach")} className="!border-0 bg-slate-900 text-white">
          <div className="flex items-center gap-2 text-amber-300"><MessageCircle className="h-4 w-4" /><Label>{t("coachTitle")}</Label></div>
          <div className="mt-1 font-display text-lg font-bold">"{t("coachPrompt")}"</div>
          <div className="mt-3 flex items-center gap-1 text-sm font-bold text-amber-300">{t("askNxt")} <ArrowRight className="h-4 w-4" /></div>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => navigate("/app/career")} data-testid="home-career-btn" className="flex items-center gap-2 rounded-2xl border border-border bg-card px-4 py-3 text-left text-sm font-bold hover:bg-secondary"><Compass className="h-4 w-4 text-indigo-600" /> {t("navCareer")}</button>
        <button onClick={() => navigate("/app/missions")} data-testid="home-quests-btn" className="flex items-center gap-2 rounded-2xl border border-border bg-card px-4 py-3 text-left text-sm font-bold hover:bg-secondary"><Trophy className="h-4 w-4 text-amber-500" /> {t("quests")} · {os.active_quests}</button>
      </div>

      {!os.is_plus && (
        <motion.button {...fade(0.36)} onClick={() => navigate("/paywall")} data-testid="home-upgrade-banner"
          className="flex w-full items-center justify-between rounded-3xl bg-gradient-to-r from-slate-900 to-indigo-900 p-5 text-left text-white transition-all hover:shadow-lg">
          <div>
            <div className="flex items-center gap-2 font-display text-lg font-bold"><Sparkles className="h-5 w-5 text-amber-300" /> {t("unlockPlus")}</div>
            <div className="text-sm text-white/70">{t("unlockSub")}</div>
          </div>
          <ArrowRight className="h-5 w-5 shrink-0" />
        </motion.button>
      )}
    </div>
  );
}
